import { JobLogValve } from 'src/utils/logs/types';

/** Describes a range of numbers, exclusive on the upper end. */
export class Range {
  public static readonly fromValve = (valve: JobLogValve): Range => {
    return new Range(valve.msElapsed - valve.duration, valve.msElapsed);
  };

  constructor(
    public readonly start: number,
    public readonly end: number,
  ) {
    if (start >= end) {
      throw new Error(`Invariant: start of range may not be at or after end of range. Start: ${start}, End: ${end}`);
    }
  }

  public get size(): number {
    return this.end - this.start;
  }

  public isContiguousWith(other: Range): boolean {
    return (
      this.doesOtherStartInsideThis(other) ||
      this.doesOtherEndInsideThis(other) ||
      this.doesThisStartInsideOther(other) ||
      this.doesThisEndInsideOther(other)
    );
  }

  public combine(other: Range): Range {
    if (!this.isContiguousWith(other)) {
      throw new Error('Cannot combine ranges, as they are non-contiguous');
    }
    return new Range(Math.min(this.start, other.start), Math.max(this.end, other.end));
  }

  private doesOtherStartInsideThis(other: Range): boolean {
    return other.start >= this.start && other.start <= this.end;
  }

  private doesOtherEndInsideThis(other: Range): boolean {
    return other.end <= this.end && other.end >= this.start;
  }

  private doesThisStartInsideOther(other: Range): boolean {
    return this.start >= other.start && this.start <= other.end;
  }

  private doesThisEndInsideOther(other: Range): boolean {
    return this.end <= other.end && this.end >= other.start;
  }
}

export class RangeSet {
  private ranges: Range[] = [];

  constructor(
    private readonly start: number,
    private readonly end: number,
  ) {
    if (start >= end) {
      throw new Error('Invariant: start of range may not be at or after end of range');
    }
  }

  public addRange(...ranges: Range[]): this {
    this.ranges.push(...ranges);
    return this.sort().clamp().flatten();
  }

  public sumIncluded(): number {
    return this.ranges.reduce((acc, val) => acc + val.size, 0);
  }

  public sumExcluded(): number {
    const totalSize = this.end - this.start;
    return totalSize - this.sumIncluded();
  }

  /** Returns a new range set where all included values are excluded, and all excluded values are included. */
  public invert(): RangeSet {
    const invertedRanges: Range[] = [];
    const invertedSet = new RangeSet(this.start, this.end);
    if (this.ranges.length === 0) {
      invertedSet.addRange(new Range(this.start, this.end));
      return invertedSet;
    }
    if (this.ranges[0].start !== this.start) {
      invertedRanges.push(new Range(this.start, this.ranges[0].start));
    }
    for (let i = 0; i < this.ranges.length - 1; i++) {
      const a = this.ranges[i];
      const b = this.ranges[i + 1];
      invertedRanges.push(new Range(a.end, b.start));
    }
    if (this.ranges[this.ranges.length - 1].end !== this.end) {
      invertedRanges.push(new Range(this.ranges[this.ranges.length - 1].end, this.end));
    }
    return invertedSet.addRange(...invertedRanges);
  }

  /** Returns a new range set where a value is included if it is present in either range. */
  public union(other: RangeSet): RangeSet {
    const resultSet = new RangeSet(Math.min(other.start, this.start), Math.max(other.end, this.end));
    return resultSet.addRange(...other.ranges, ...this.ranges);
  }

  /** Returns a new range set where a value is included if it is present in both ranges. */
  public intersect(other: RangeSet): RangeSet {
    const resultSet = new RangeSet(Math.min(other.start, this.start), Math.max(other.end, this.end));
    return resultSet.addRange(...other.invert().ranges, ...this.invert().ranges).invert();
  }

  private sort(): this {
    this.ranges.sort((a, b) => {
      if (a.start < b.start) return -1;
      if (b.start < a.start) return 1;
      return 0;
    });
    return this;
  }

  public getRanges() {
    return [...this.ranges];
  }

  public getRangesBeforePointInTime(pointInTime: number) {
    return this.ranges.filter(range => range.start < pointInTime);
  }

  private flatten(): this {
    if (this.ranges.length === 0) return this;

    const result: Range[] = [this.ranges[0]];
    for (let i = 1; i < this.ranges.length; i++) {
      const range = this.ranges[i];
      const lastResult = result.length - 1;
      if (result[lastResult].isContiguousWith(range)) {
        result[lastResult] = result[lastResult].combine(range);
      } else {
        result.push(range);
      }
    }
    this.ranges = result;
    return this;
  }

  private clamp(): this {
    for (let i = this.ranges.length - 1; i >= 0; i--) {
      const range = this.ranges[i];
      if (range.start < this.start) {
        if (range.end <= this.start) {
          this.ranges.splice(i, 1);
        } else {
          this.ranges.splice(i, 1, new Range(this.start, range.end));
        }
      } else if (range.end > this.end) {
        if (range.start >= this.end) {
          this.ranges.splice(i, 1);
        } else {
          this.ranges.splice(i, 1, new Range(range.start, this.end));
        }
      }
    }
    return this;
  }
}
